import Application from './Application';
import common from './common';
import PreloaderScene from './scenes/PreloaderScene';
import Assets from './Assets';
import LanguageManager from './utils/LanguageManager';
import * as THREE from 'three';
import SaveData from './SaveData';
import Logger from './utils/Logger';

export default class Main {
	constructor(width = 1024, height = 768) {
		this._width = width;
		this._height = height;
		this._app = null;
		this._preloader = null;
		this._startReady = false;
		this._view = null;

		common.AssetManager = null;

		this._globeOptions = {
			width: (700 * 1.2),
			height: (700 * 1.2),
		};
	}

	init() {
		PIXI.settings.MIPMAP_TEXTURES = false;
		PIXI.settings.RETINA_PREFIX = /_(?=[^_]*$)(.+)x/;
		PIXI.settings.STRICT_TEXTURE_CACHE = true;
		PIXI.Text.nextLineHeightBehavior = true;
		PIXI.Text.experimentalLetterSpacing = false;

		P3.assets.audioFileTypes = ['mp4'];
		P3.settings.WARN_TEXTURE_DIVISIBLE_BY_2 = false;
		P3.settings.WARN_TEXTURE_MISSING = true;

		THREE.Cache.enabled = false;

		let hidden;

		typeof document.hidden !== 'undefined' ? (hidden = 'hidden',
		this.visibilityChange = 'visibilitychange') : typeof document.mozHidden !== 'undefined' ? (hidden = 'mozHidden',
		this.visibilityChange = 'mozvisibilitychange') : typeof document.msHidden !== 'undefined' ? (hidden = 'msHidden',
		this.visibilityChange = 'msvisibilitychange') : typeof document.webkitHidden !== 'undefined' && (hidden = 'webkitHidden',
		this.visibilityChange = 'webkitvisibilitychange');

		document.addEventListener(this.visibilityChange, function () {
			document[hidden] ? Howler.volume(0) : Howler.volume(1);
		}, false);

		common.tracking = new P3.Tracking(new P3.TrackingModuleGtag('G-79ZRJ9HW4B'));

		const query = window.location.search;

		if (query) {
			const params = new URLSearchParams(query);

			common.debug = params.get('debug') === 'true';
		}

		common.retina = this._getRetina();

		const options = {
			width: this._width,
			height: this._height,
			holderId: 'game',
			portrait: false,
			rotateImageUrl: 'assets/images/rotate.jpg',
			rotateImageColor: '#000000',
			aspectRatioFillHeight: true,
			onLoadLegacy: false,
			onResizeLegacy: false,
		};

		this._view = new P3.View(options);

		this._view.signals.ready.once((canvas) => {
			/**
			 * THREE Init
			 */
			const tRenderer = new THREE.WebGLRenderer({ alpha: true });

			tRenderer.autoClear = true;

			const tScene = new THREE.Scene();

			const tCamera = new THREE.PerspectiveCamera(60, 1, 0.1, 1000);

			const assetManager = new Assets();

			assetManager.signals.complete.once(this.onLoadThreeAssets, this);

			common.tRenderer = tRenderer;
			common.tScene = tScene;
			common.tCamera = tCamera;
			common.AssetManager = assetManager;

			/**
			 * PIXI Init
			 */
			const stage = new PIXI.Container();

			common.stage = stage;

			const input = P3.input;

			input.init(stage, canvas);

			const options = {};

			options.width = this._width;
			options.height = this._height;
			options.view = canvas;
			options.antialias = false;
			options.resolution = 2;
			options.backgroundColor = 0x0;

			// initialize the PIXI renderer
			const renderer = PIXI.autoDetectRenderer(options);

			common.renderer = renderer;

			$('#game').prepend(tRenderer.domElement);
			$('#game > canvas:nth-child(1)').css({ display: 'none', 'z-index': 99999, position: 'absolute', left: '0px', right: '0px', top: '0px', bottom: '0px', width: '100%', height: '100%', overflow: 'visible', touchAction: 'none', cursor: 'inherit' });
			$('#game > canvas:nth-child(1)').attr('id', 'three-canvas');

			// we add the default scene managers view to the display list
			const scene = P3.scene;

			common.stage.addChild(scene.view);

			// we add our transitions to this to ensure they are always on top of everything
			const transitions = new PIXI.Container();

			common.stage.addChild(transitions);
			common.transitions = transitions;

			// we stop the shared ticker that drives animation/rendering
			const ticker = PIXI.Ticker.shared;

			// update game accordingly
			ticker.add((delta) => {
				window.dt = delta / PIXI.settings.TARGET_FPMS / 1000;

				scene.update();
				input.update();

				TWEEN.update();

				tRenderer.render(tScene, tCamera);
				renderer.render(stage);
			});

			this.loadPreAssets();
			try {
				const params = new URLSearchParams(window.location.search);

				if (params.get('debugMemory') === 'true') {
					// Defer until next microtask to ensure global PIXI/common are ready
					setTimeout(() => {
						Promise.all([
							import('./utils/MemoryTracker'),
							import('./ui/MemoryOverlay'),
						]).then(([, overlayMod]) => {
							if (overlayMod && typeof overlayMod.startMemoryOverlay === 'function') {
								overlayMod.startMemoryOverlay(() => ({ stage }));
							}
						}).catch(() => {
							// Silent: debug helper failed to load; ignore in production
						});
					}, 0);
				}
			} catch (e) { /* no-op */ }
		});
		this._view.signals.resize.add(this.onCanvasResize, this);

		this.loadConfig(() => this._view.init());
	}

	loadConfig(callback) {
		const basePath = './';

		P3.assets.addFiles([
			{ name: 'localisation', url: 'localisation.json' },
			{ name: 'manifest', url: `assets/manifest_${common.retina}.json` },
			{ name: 'config', url: 'config.json' },
		], basePath);

		P3.assets.signals.complete.once(() => {
			common.config = P3.assets.json('config');
			common.i18nConfig = P3.assets.json('localisation');

			const save = new SaveData();

			save.loadGame();
			common.save = save;

			callback && callback.call();
		});
		P3.assets.load();
	}

	loadPreAssets() {
		const files = [
			{ name: 'preloader_bg', url: 'images/bg_preloader.jpg' },
			{ name: 'loading', url: 'images/loading.json' },
		];

		if (files.length) {
			const basePath = './';

			P3.assets.addFiles([{ name: `language_${common.config.language}`, url: `localisation/${common.config.language}.json` }], basePath);
			P3.assets.addFiles(files, `${basePath}assets/`);
			P3.assets.signals.complete.once(() => {
				common.languageManager = new LanguageManager();
				common.languageManager.init();

				this.loadAssets();
			}, this);
			P3.assets.load();
		} else {
			this.loadAssets();
		}
	}

	loadAssets() {
		const files = [
			...P3.assets.json('manifest').shared,
			...P3.assets.json('manifest').splash,
		];

		const threeFiles = [
			{ name: 'earth_globe', path: './assets/3d/earth.glb', type: '3d' },
		];

		if (files.length) {
			this._preloader = new PreloaderScene();
			P3.scene.show(this._preloader);

			common.AssetManager.addFiles(threeFiles);

			const basePath = './';

			P3.assets.addFiles(files, `${basePath}assets/`);
			P3.assets.signals.progress.add(this.onLoadingProgress, this);
			P3.assets.signals.complete.once(this.onLoadingCompleted, this);
			P3.assets.load();
		} else {
			this.start();
		}
	}

	onLoadThreeAssets() {
		this.start();
	}

	async start() {
		this._app = new Application();
		this._app.init();
	}

	onLoadingProgress(event) {
		this._preloader.loaded = event.progress / 100;
	}

	onLoadingCompleted() {
		this._preloader && (this._preloader.loaded = 1);
		common.AssetManager.loadFiles();
	}

	onCanvasResize(correct) {
		if (correct) {
			const zoom = P3.View.zoom;

			common.STAGE_WIDTH = 2080 * zoom;
			common.STAGE_HEIGHT = 960 * zoom;
			common.renderer.resize(P3.View.width, P3.View.height);

			common.tRenderer.setSize(this._globeOptions.width * zoom, this._globeOptions.height * zoom);
			common.tCamera.aspect = 1;
			common.tCamera.updateProjectionMatrix();

			if (P3.scene) {
				P3.scene.resize();

				for (let i = 0; i < common.transitions.children.length; ++i) {
					const transition = common.transitions.children[i];

					transition.resize && transition.resize();
				}
			}
		}
	}

	/* https://www.w3schools.com/js/js_cookies.asp */
	getCookie(cname) {
		const name = `${cname}=`;
		const decodedCookie = decodeURIComponent(document.cookie);
		const ca = decodedCookie.split(';');

		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];

			while (c.charAt(0) === ' ') {
				c = c.substring(1);
			}
			if (c.indexOf(name) === 0) {
				return c.substring(name.length, c.length);
			}
		}

		return '';
	}

	_getRetina() {
		const userAgent = navigator.userAgent;
		let textureSize = '1x';

		const androidMatch = userAgent.match(/Android\s+(\d+)(?:\.(\d+))?/i);

		if (androidMatch) {
			const versionNumber = parseInt(androidMatch[1], 10);

			if (!Number.isNaN(versionNumber) && versionNumber < 11) {
				textureSize = '0.5x';
			}
		} else if (userAgent.includes('iPad') || userAgent.includes('iPhone')) {
			const s = userAgent.substring(userAgent.indexOf('OS') + 3, 1e4);
			const versionNumber = parseInt(s.substring(0, s.indexOf('_')));

			if (versionNumber) {
				if (versionNumber < 13) {
					textureSize = '0.5x';
				}
			}
		}

		Logger.log('Texture size:', textureSize);

		return textureSize;
	}

	setCookie(cname, cvalue) {
		document.cookie = `${cname}=${cvalue};path=/`;
	}
}
